/**
 * SSB scroll reveal.
 *
 * Marks elements as they enter the viewport so CSS can ease them in. Kept
 * deliberately small and safe:
 *
 *  - The hidden starting state is applied only once this script has set
 *    `data-ssb-reveal-ready` on <html>. If the script fails to load, nothing
 *    is ever hidden and the page reads normally.
 *  - Anything already on screen at load reveals immediately with no delay, so
 *    above-the-fold content never waits on a scroll that may not come.
 *  - Reduced motion opts out entirely — no observer, no transitions.
 *  - Elements are unobserved once revealed; it never re-hides on scroll up.
 *
 * Usage:
 *   <div data-ssb-reveal>                   reveals itself
 *   <ul data-ssb-reveal="children">         staggers its direct children
 */
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Children of a horizontally scrolling track cannot be staggered.
 *
 * IntersectionObserver measures against the viewport, so a card parked outside
 * the scroller's visible run never intersects and would stay hidden forever
 * unless the visitor happened to scroll sideways. Such a host reveals as one
 * unit instead.
 */
function isHorizontalScroller(el) {
  const overflowX = getComputedStyle(el).overflowX;
  return (
    (overflowX === 'auto' || overflowX === 'scroll') &&
    el.scrollWidth > el.clientWidth + 1
  );
}

function collect() {
  const targets = [];
  for (const host of document.querySelectorAll('[data-ssb-reveal]')) {
    const staggerChildren =
      host.dataset.ssbReveal === 'children' && !isHorizontalScroller(host);

    if (staggerChildren) {
      Array.from(host.children).forEach((el, i) => targets.push({ el, index: i }));
    } else {
      targets.push({ el: host, index: 0 });
    }
  }
  return targets;
}

function init() {
  // Motion is unwelcome: leave every element in its natural state.
  if (REDUCED.matches) return;

  document.documentElement.setAttribute('data-ssb-reveal-ready', '');

  const targets = collect();
  if (!targets.length) return;

  for (const { el, index } of targets) {
    el.classList.add('ssb-reveal');
    el.style.setProperty('--ssb-reveal-index', String(index));
  }

  // No IntersectionObserver (very old browser): show everything at once.
  if (!('IntersectionObserver' in window)) {
    targets.forEach(({ el }) => el.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    // Start slightly before the element is fully in view so the motion has
    // finished by the time it reaches comfortable reading position.
    { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
  );

  const viewportHeight = window.innerHeight;
  for (const { el } of targets) {
    // Already visible at load: reveal now, and drop the stagger so the first
    // screen does not animate in as a slow cascade.
    if (el.getBoundingClientRect().top < viewportHeight * 0.9) {
      el.style.setProperty('--ssb-reveal-index', '0');
      requestAnimationFrame(() => el.classList.add('is-revealed'));
    } else {
      observer.observe(el);
    }
  }

  // If the visitor turns reduced motion on mid-session, stop hiding anything.
  REDUCED.addEventListener('change', (e) => {
    if (!e.matches) return;
    observer.disconnect();
    document.documentElement.removeAttribute('data-ssb-reveal-ready');
    targets.forEach(({ el }) => el.classList.add('is-revealed'));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
